import React, { useState } from "react";
import { distributorService } from "../services/apiService";

const cls = (err) =>
  `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none ${
    err ? "border-red-400 bg-red-50" : "border-blue-300"
  }`;

const DistributorBillPopup = ({ billNo, mobileNo, onSave, onClose, onBillNoChange, onMobileNoChange, isEditMode = false, distributorId = null, billId = null }) => {
  const [loading, setLoading] = useState(false);
  const [distributorFound, setDistributorFound] = useState(false);
  const [distributorIdState, setDistributorIdState] = useState(distributorId);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const [distributorForm, setDistributorForm] = useState({
    name: "",
    mobileNo: mobileNo || "",
    address: ""
  });
  
  const [billForm, setBillForm] = useState({
    billNo: billNo || "",
    paymentType: "",
    transactionId: "",
    paidAmount: "",
    billAmount: "",
    restAmount: "",
    distributorGstin: "",
    supplyType: "INTRA",
  });
  
  const [errors, setErrors] = useState({});
  const [saveDistributorFlag, setSaveDistributorFlag] = useState(false);
  const [billFoundId, setBillFoundId] = useState(billId || null);

  // Fetch distributor on mount
  React.useEffect(() => {
    if (isEditMode && distributorId && billId) {
      fetchDistributorWithBill(distributorId, billId);
    } else if (mobileNo && mobileNo.trim()) {
      fetchDistributor(mobileNo.trim());
    } else {
      // If no mobile provided, set forms with initial values
      setDistributorForm(prev => ({ ...prev, mobileNo: mobileNo || "" }));
      setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
      setSaveDistributorFlag(true);
    }
  }, []);

  const fetchDistributorWithBill = async (distId, bId) => {
    setLoading(true);
    try {
      const response = await distributorService.getDistributorWithBill(distId, bId);
      if (response) {
        setDistributorFound(true);
        setDistributorIdState(response.distributor.distributorId);
        setSaveDistributorFlag(false);
        setDistributorForm({
          name: response.distributor.distributorName,
          mobileNo: response.distributor.contactNumber,
          address: response.distributor.address
        });
        
        const billAmount = parseFloat(response.bill.billAmount) || 0;
        const paidAmount = parseFloat(response.bill.paidAmount) || 0;
        const restAmount = (billAmount - paidAmount).toFixed(2);
        
        setBillForm({
          billNo: response.bill.billNumber,
          paymentType: response.bill.paymentStatus,
          transactionId: response.bill.transactionId || "",
          paidAmount: response.bill.paidAmount ? response.bill.paidAmount.toString() : "",
          billAmount: response.bill.billAmount.toString(),
          restAmount: restAmount,
          distributorGstin: response.bill.distributorGstin || "",
          supplyType: response.bill.supplyType || "INTRA",
        });
      }
    } catch (err) {
      console.error("Error fetching distributor with bill:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributor = async (mobile) => {
    setLoading(true);
    try {
      const response = await distributorService.getByMobile(mobile);
      if (response) {
        setDistributorFound(true);
        setDistributorIdState(response.distributorId);
        setSaveDistributorFlag(false);
        setDistributorForm({
          name: response.distributorName,
          mobileNo: response.contactNumber,
          address: response.address
        });

        // Check if bill already exists for this distributor + billNo
        if (billNo && billNo.trim()) {
          try {
            const billResponse = await distributorService.getBillByNumber(billNo.trim(), response.distributorId);
            if (billResponse && billResponse.bill) {
              // Bill exists — populate bill form for UPDATE
              setBillFoundId(billResponse.bill.billId || billResponse.bill.id);
              const billAmount = parseFloat(billResponse.bill.billAmount) || 0;
              const paidAmount = parseFloat(billResponse.bill.paidAmount) || 0;
              setBillForm({
                billNo: billResponse.bill.billNumber || billNo,
                paymentType: billResponse.bill.paymentStatus || "",
                transactionId: billResponse.bill.transactionId || "",
                paidAmount: billResponse.bill.paidAmount ? billResponse.bill.paidAmount.toString() : "",
                billAmount: billResponse.bill.billAmount ? billResponse.bill.billAmount.toString() : "",
                restAmount: (billAmount - paidAmount).toFixed(2)
              });
            } else {
              setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
            }
          } catch {
            // Bill not found — new bill creation
            setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
          }
        } else {
          setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
        }
      } else {
        setDistributorFound(false);
        setSaveDistributorFlag(true);
        setDistributorForm({ name: "", mobileNo: mobile, address: "" });
        setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
      }
    } catch (err) {
      setDistributorFound(false);
      setSaveDistributorFlag(true);
      setDistributorForm({ name: "", mobileNo: mobile, address: "" });
      setBillForm(prev => ({ ...prev, billNo: billNo || "" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDistributorChange = (e) => {
    const { name, value } = e.target;
    setDistributorForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    
    // Sync mobile no to parent
    if (name === "mobileNo") {
      onMobileNoChange(value);
    }
  };

  const handleBillChange = (e) => {
  const { name, value } = e.target;

  setBillForm(prev => {
    const updated = { ...prev, [name]: value };

    // If payment type is NA
    if (name === "paymentType" && value === "NA") {
      updated.paidAmount = "0";

      const bill = parseFloat(updated.billAmount) || 0;
      updated.restAmount = bill.toFixed(2);
    }

    // Recalculate rest amount
    if (name === "billAmount" || name === "paidAmount") {
      const bill =
        parseFloat(name === "billAmount" ? value : updated.billAmount) || 0;

      const paid =
        parseFloat(name === "paidAmount" ? value : updated.paidAmount) || 0;

      updated.restAmount = (bill - paid).toFixed(2);
    }

    // If bill amount changes while payment type is NA
    if (name === "billAmount" && updated.paymentType === "NA") {
      const bill = parseFloat(value) || 0;
      updated.paidAmount = "0";
      updated.restAmount = bill.toFixed(2);
    }

    return updated;
  });

  if (errors[name]) {
    setErrors(prev => ({ ...prev, [name]: "" }));
  }

  if (name === "billNo") {
    onBillNoChange(value);
  }
};

  const validate = () => {
    const newErrors = {};
    
    // Distributor validation
    if (!distributorForm.name.trim()) newErrors.name = "Name is required";
    if (!distributorForm.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile no is required";
    } else if (!/^\d{10}$/.test(distributorForm.mobileNo.trim())) {
      newErrors.mobileNo = "Mobile number must be 10 digits";
    }
    if (!distributorForm.address.trim()) newErrors.address = "Address is required";
    
    // Bill validation
    if (!billForm.billNo.trim()) newErrors.billNo = "Bill no is required";
    if (!billForm.paymentType) newErrors.paymentType = "Payment type is required";
    if (!billForm.billAmount || parseFloat(billForm.billAmount) <= 0) newErrors.billAmount = "Bill amount is required";
    if (
  billForm.paymentType !== "NA" &&
  (billForm.paidAmount === "" ||
    parseFloat(billForm.paidAmount) < 0)
) {
  newErrors.paidAmount = "Paid amount is required";
}
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setLoading(true);
    try {
      if ((isEditMode || billFoundId) && distributorIdState && (billId || billFoundId)) {
        // Update mode - call PUT endpoints
        const distributorUpdatePayload = {
          distributorId: distributorIdState,
          distributorName: distributorForm.name,
          contactNumber: distributorForm.mobileNo,
          email: "",
          address: distributorForm.address,
          status: "ACTIVE"
        };
        
        const billUpdatePayload = {
          billId: billId || billFoundId,
          distributorId: distributorIdState,
          billNumber: billForm.billNo,
          billAmount: parseFloat(billForm.billAmount),
          billDate: new Date().toISOString(),
          paymentStatus: billForm.paymentType,
          transactionId: billForm.transactionId.trim() || null,
          paidAmount: parseFloat(billForm.paidAmount) || 0,
          distributorGstin: billForm.distributorGstin.trim() || null,
          supplyType: billForm.supplyType || "INTRA",
        };
        
        await distributorService.updateDistributor(distributorUpdatePayload);
        await distributorService.updateBill(billUpdatePayload);
        onSave(billId || billFoundId, distributorIdState, billUpdatePayload.supplyType);
      } else {
        // Create mode - call POST endpoint
        const requestPayload = {
          saveDistributorFlag,
          distributorDetails: {
            distributorName: distributorForm.name,
            contactNumber: distributorForm.mobileNo,
            email: "",
            address: distributorForm.address,
            status: "ACTIVE"
          },
          billDetails: {
            billNumber: billForm.billNo,
            billAmount: parseFloat(billForm.billAmount),
            billDate: new Date().toISOString(),
            paymentStatus: billForm.paymentType,
            transactionId: billForm.transactionId.trim() || null,
            paidAmount: parseFloat(billForm.paidAmount) || 0,
            distributorGstin: billForm.distributorGstin.trim() || null,
            supplyType: billForm.supplyType || "INTRA",
          }
        };
        
        const response = await distributorService.createBillWithDistributor(requestPayload);
        onSave(response.billId, response.distributorId, billForm.supplyType || "INTRA");
      }
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.errorMessage || "Failed to save distributor bill");
    } finally {
      setLoading(false);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] md:max-w-6xl border border-blue-200 max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          transform: window.innerWidth >= 768 ? `translate(${position.x}px, ${position.y}px)` : undefined,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0 cursor-move"
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-file-invoice text-indigo-600" />
            <h3 className="font-bold text-blue-800 text-lg">Distributor Bill Details</h3>
          </div>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800 transition-colors" onMouseDown={(e) => e.stopPropagation()}>
            <i className="fas fa-times text-xl" />
          </button>
        </div>

        {/* Form content */}
        <div className="p-5 overflow-y-auto flex-1" style={{maxHeight: 'calc(90vh - 180px)'}}>
          <form onSubmit={handleSubmit} id="distributorBillForm">
            {/* Distributor Details Section */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="fas fa-user text-indigo-500" />
                Distributor Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Distributor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={distributorForm.name}
                    onChange={handleDistributorChange}
                    readOnly={distributorFound}
                    className={cls(errors.name) + (distributorFound ? " bg-gray-100 cursor-not-allowed" : "")}
                    placeholder="Enter distributor name"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobileNo"
                    value={distributorForm.mobileNo}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleDistributorChange({ target: { name: 'mobileNo', value } });
                    }}
                    readOnly={distributorFound}
                    maxLength="10"
                    className={cls(errors.mobileNo) + (distributorFound ? " bg-gray-100 cursor-not-allowed" : "")}
                    placeholder="Enter mobile number"
                  />
                  {errors.mobileNo && <p className="text-red-500 text-xs mt-1">{errors.mobileNo}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={distributorForm.address}
                    onChange={handleDistributorChange}
                    readOnly={distributorFound}
                    className={cls(errors.address) + (distributorFound ? " bg-gray-100 cursor-not-allowed" : "")}
                    placeholder="Enter address"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                </div>
              </div>
            </div>

            {/* Decorative Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gradient-to-r from-indigo-200 via-blue-300 to-teal-200" style={{borderImage: "linear-gradient(to right, #c7d2fe, #93c5fd, #99f6e4) 1"}}></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-gray-500 flex items-center gap-2">
                  <i className="fas fa-arrow-down text-indigo-400"></i>
                  Bill Information
                  <i className="fas fa-arrow-down text-indigo-400"></i>
                </span>
              </div>
            </div>

            {/* Distributor Bill Details Section */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="fas fa-receipt text-green-500" />
                Bill Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Bill No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="billNo"
                    value={billForm.billNo}
                    onChange={handleBillChange}
                    className={cls(errors.billNo)}
                    placeholder="Enter bill number"
                  />
                  {errors.billNo && <p className="text-red-500 text-xs mt-1">{errors.billNo}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Transaction Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="paymentType"
                    value={billForm.paymentType}
                    onChange={handleBillChange}
                    className={cls(errors.paymentType)}
                  >
                    <option value="">Select payment type</option>
                    <option value="NA">NA</option>
                    <option value="CASH">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Card</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                  {errors.paymentType && <p className="text-red-500 text-xs mt-1">{errors.paymentType}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    name="transactionId"
                    value={billForm.transactionId}
                    onChange={handleBillChange}
                    className={cls(false)}
                    placeholder="Enter transaction ID"
                  />
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Bill Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="billAmount"
                    value={billForm.billAmount}
                    onChange={handleBillChange}
                    step="0.01"
                    min="0"
                    className={cls(errors.billAmount)}
                    placeholder="0.00"
                  />
                  {errors.billAmount && <p className="text-red-500 text-xs mt-1">{errors.billAmount}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Paid Amount <span className="text-red-500">*</span>
                  </label>
                  <input
  type="number"
  name="paidAmount"
  value={billForm.paidAmount}
  onChange={handleBillChange}
  step="0.01"
  min="0"
  readOnly={billForm.paymentType === "NA"}
  className={
    cls(errors.paidAmount) +
    (billForm.paymentType === "NA"
      ? " bg-gray-100 cursor-not-allowed"
      : "")
  }
  placeholder="0.00"
/>
                  {errors.paidAmount && <p className="text-red-500 text-xs mt-1">{errors.paidAmount}</p>}
                </div>
                
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Rest Amount
                  </label>
                  <input
                    type="text"
                    value={billForm.restAmount}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 cursor-not-allowed"
                    placeholder="0.00"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Distributor GSTIN
                  </label>
                  <input
                    type="text"
                    name="distributorGstin"
                    value={billForm.distributorGstin}
                    onChange={handleBillChange}
                    maxLength={20}
                    className={cls(false)}
                    placeholder="e.g. 27AAPFU0939F1ZV"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Supply Type
                  </label>
                  <select
                    name="supplyType"
                    value={billForm.supplyType}
                    onChange={handleBillChange}
                    className={cls(false)}
                  >
                    <option value="INTRA">INTRA (Within State)</option>
                    <option value="INTER">INTER (Outside State)</option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="distributorBillForm"
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-white font-semibold text-sm ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin" /> {isEditMode ? "Updating..." : "Saving..."}</>
            ) : (
              <><i className="fas fa-save" /> {(isEditMode || billFoundId) ? "Update" : "Save"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistributorBillPopup;
